/**
 * VaultAuditLog — Owner-only audit trail viewer for vault operations.
 * Shows who decrypted which sections, key rotations, member revocations.
 */
import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Shield, Key, UserMinus, Eye, RefreshCw, Loader2, Lock, Unlock } from 'lucide-react';
import { getVaultAuditLog } from '@/services/vaultService';

const ACTION_CONFIG = {
  decrypt_section: { icon: Unlock, color: 'text-blue-400', label: 'Decrypted' },
  encrypt_section: { icon: Lock, color: 'text-emerald-400', label: 'Encrypted' },
  key_rotation: { icon: RefreshCw, color: 'text-amber-400', label: 'Key Rotation' },
  share_keys: { icon: Key, color: 'text-purple-400', label: 'Shared Keys' },
  revoke_keys: { icon: UserMinus, color: 'text-red-400', label: 'Revoked Access' },
  update_keys: { icon: Key, color: 'text-orange-400', label: 'Updated Keys' },
  migrate_v2: { icon: Shield, color: 'text-emerald-400', label: 'Migrated to V2' },
};

function getActionConfig(action) {
  return ACTION_CONFIG[action] || { icon: Eye, color: 'text-[var(--text-muted)]', label: action };
}

function formatTime(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export default function VaultAuditLog({ projectId }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchLog = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getVaultAuditLog(projectId);
      setEntries(data.entries || []);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('Only the project owner can view the vault audit log.');
      } else {
        setError('Failed to load audit log');
      }
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchLog();
  }, [fetchLog]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 size={20} className="animate-spin text-[var(--text-muted)]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <Shield size={24} className="mx-auto mb-2 text-[var(--text-disabled)]" />
        <p className="text-xs text-[var(--text-muted)]">{error}</p>
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="text-center py-12">
        <Shield size={24} className="mx-auto mb-2 text-[var(--text-disabled)]" />
        <p className="text-xs text-[var(--text-muted)]">No vault audit events yet</p>
        <p className="text-[10px] text-[var(--text-disabled)] mt-1">
          Events are logged when sections are encrypted, decrypted, or shared.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-xs font-semibold text-[var(--text-secondary)] flex items-center gap-1.5">
          <Shield size={12} />
          Vault Audit Log
        </h4>
        <button
          onClick={fetchLog}
          className="p-1 rounded hover:bg-[var(--bg-elevated)] text-[var(--text-disabled)] hover:text-[var(--text-secondary)]"
        >
          <RefreshCw size={12} />
        </button>
      </div>

      <div className="space-y-0.5 max-h-[400px] overflow-y-auto">
        {entries.map((entry, i) => {
          const config = getActionConfig(entry.action);
          const Icon = config.icon;

          return (
            <motion.div
              key={entry.id || i}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex items-center gap-2.5 py-1.5 px-2 rounded-lg hover:bg-[rgba(24,24,27,0.5)] transition-colors"
            >
              <Icon size={13} className={config.color} />
              <div className="flex-1 min-w-0">
                <span className="text-[11px] text-[var(--text-secondary)]">
                  {config.label}
                  {entry.section && (
                    <span className="text-[var(--text-muted)]"> — {entry.section}</span>
                  )}
                </span>
                {entry.metadata?.user_email && (
                  <span className="text-[10px] text-[var(--text-disabled)] ml-1.5">
                    by {entry.metadata.user_email}
                  </span>
                )}
              </div>
              <span className="text-[10px] text-[var(--text-disabled)] whitespace-nowrap flex-shrink-0">
                {formatTime(entry.timestamp)}
              </span>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
